import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import pick from 'lodash/pick';

import { UiButton } from 'src/components/UI/Button';
import { ChildrenPropType } from 'src/utils/types';
import { getSearchParams, setSearchParam } from 'src/utils/url';
import { useQuery } from 'src/utils/hooks/useQuery';

export type UiTabsRefType = {
  changeTab: (ids: UiTabsPropsType['items'][0]['id']) => () => void;
};

export type UiTabsPropsType = {
  items: {
    id: string | string[];
    title: string;
    content: ChildrenPropType;
  }[];

  contentClassName?: string;
  defaultActiveTab?: string; // id of item
  withoutSearchParam?: boolean;
  noClearParams?: string[];
};

export const uiTabQueryParam = 'tab';

export const UiTabs = forwardRef<UiTabsRefType, UiTabsPropsType>(
  ({ items, defaultActiveTab, contentClassName, withoutSearchParam, noClearParams }, ref) => {
    const getInitialActiveTab = useCallback(() => {
      const simpleValue = defaultActiveTab || (!withoutSearchParam && getSearchParams(uiTabQueryParam));

      if (simpleValue) {
        return simpleValue;
      }

      if (items.length) {
        const ids = items[0].id;

        return Array.isArray(ids) ? ids[0] : ids;
      }

      return '';
    }, [defaultActiveTab, items, withoutSearchParam]);

    const [activeTab, setActiveTab] = useState(getInitialActiveTab);

    const checkCurrentTab = useCallback(
      (ids: string | string[]) => (Array.isArray(ids) ? ids.includes(activeTab as string) : activeTab === ids),
      [activeTab]
    );

    const currentContent = useMemo(
      () => items.find(({ id }) => checkCurrentTab(id))?.content || 'Tab content not found',
      [checkCurrentTab, items]
    );

    const onChangeTab = useCallback(
      (ids: UiTabsPropsType['items'][0]['id']) => () => {
        const id = Array.isArray(ids) ? ids[0] : ids;

        setActiveTab(id);

        let params = {
          [uiTabQueryParam]: id,
        };

        if (noClearParams?.length) {
          params = { ...params, ...pick(getSearchParams(), noClearParams) };
        }

        if (!withoutSearchParam) {
          setSearchParam(params, true, true);
        }
      },
      [noClearParams, withoutSearchParam]
    );

    const selectedTab = useQuery(uiTabQueryParam);

    useEffect(() => {
      if (!withoutSearchParam) {
        setActiveTab(selectedTab || getInitialActiveTab());
      }
    }, [getInitialActiveTab, selectedTab, withoutSearchParam]);

    useImperativeHandle(ref, () => ({ changeTab: onChangeTab }));

    return (
      <>
        <div className="tabs">
          {items.map(({ title, id }) => (
            <UiButton
              key={id.toString()}
              tag="a"
              className={`tab${checkCurrentTab(id) ? ' active' : ''}`}
              onClick={onChangeTab(id)}
            >
              {title}
            </UiButton>
          ))}
        </div>
        <div className={contentClassName}>{currentContent}</div>
      </>
    );
  }
);
